import { FC, useEffect, useState } from 'react';
import { Form, Input, TreeSelect, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
    createExpenseGroupAction,
    updateExpenseGroupAction,
    getExpenseGroupsAction,
} from 'redux/action/expenseGroupAction';
import { classOptionsAction } from 'redux/action/classAction';
import { chartOfAccountAction } from 'redux/action/chartOfAccountAction';
import { customerOptionsAction } from 'redux/action/customerAction';
import { locationOptionsAction } from 'redux/action/locationAction';
import { vendorAction } from 'redux/action/vendorAction';
import styles from './index.module.scss';
import { getOptionLabel } from 'utils/utils';
import { ExpenseGroup } from '../ExpenseGroupsTable';


interface AddEditExpenseGroupProps {
    closeDrawerByAnimation: () => void;
    isEditMode: boolean;
    expenseGroup: ExpenseGroup | null;
}

// Helper function to transform Redux data to TreeSelect format (recursive for nested children)
const transformToTreeData = (data: any[]): any[] => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data
        .filter((item: any) => item && item.Id)
        .map((item: any): any => ({
            title: item.title || item.Name || item.DisplayName || item.FullyQualifiedName,
            value: item.Id,
            key: item.Id,
            disabled: item.disabled === true,
            children: item.children && Array.isArray(item.children) && item.children.length > 0
                ? transformToTreeData(item.children)
                : [],
        }));
};


const AddEditExpenseGroup: FC<AddEditExpenseGroupProps> = ({
    closeDrawerByAnimation,
    isEditMode,
    expenseGroup
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const [operationCompleted, setOperationCompleted] = useState(false);
    const [accountsTooltipDisabled, setAccountsTooltipDisabled] = useState(false);
    const [customersTooltipDisabled, setCustomersTooltipDisabled] = useState(false);
    const [classesTooltipDisabled, setClassesTooltipDisabled] = useState(false);
    const [locationsTooltipDisabled, setLocationsTooltipDisabled] = useState(false);
    const [vendorsTooltipDisabled, setVendorsTooltipDisabled] = useState(false);

    // Redux state
    const { isUpdateLoading: loading, error } = useSelector((state: any) => state.expenseGroups);

    // Redux state for dropdown data
    const { optionData: qbClass } = useSelector((state: any) => state.class);
    const { data: qbChartOfAccounts } = useSelector((state: any) => state.chartOfAccounts);
    const { optionData: qbCustomers } = useSelector((state: any) => state.customer);
    const { optionData: qbLocations } = useSelector((state: any) => state.locations);
    const { data: qbVendors } = useSelector((state: any) => state.vendors);

    // Transform Redux data to TreeSelect format
    const treeData = {
        accounts: transformToTreeData(qbChartOfAccounts || []),
        vendors: transformToTreeData(qbVendors || []),
        customers: [
            {
                title: 'Blank Customer',
                value: '',
                key: 'blank',
                disabled: false,
                children: []
            },
            ...transformToTreeData(qbCustomers || [])
        ],
        classes: [
            {
                title: 'Blank Class',
                value: '',
                key: 'blank',
                disabled: false,
                children: []
            },
            ...transformToTreeData(qbClass || [])
        ],
        locations: [
            {
                title: 'Blank Location',
                value: '',
                key: 'blank',
                disabled: false,
                children: []
            },
            ...transformToTreeData(qbLocations || [])
        ]
    };

    // Fetch dropdown data on component mount
    useEffect(() => {
        dispatch(classOptionsAction());
        dispatch(chartOfAccountAction());
        dispatch(customerOptionsAction());
        dispatch(locationOptionsAction());
        dispatch(vendorAction());
        setOperationCompleted(false); // Reset operation flag on mount
    }, [dispatch]);

    useEffect(() => {
        if (isEditMode && expenseGroup) {
            // Transform '-' back to '' for display purposes
            const transformedCustomers = expenseGroup.customers?.map((customer: string) =>
                customer === 'blank' ? '' : customer
            ) || [];
            const transformedClasses = expenseGroup.classes?.map((classItem: string) =>
                classItem === 'blank' ? '' : classItem
            ) || [];
            const transformedLocations = expenseGroup.locations?.map((location: string) =>
                location === 'blank' ? '' : location
            ) || [];

            form.setFieldsValue({
                name: expenseGroup.name,
                description: expenseGroup.description,
                accounts: expenseGroup.accounts,
                customers: transformedCustomers,
                classes: transformedClasses,
                locations: transformedLocations,
                vendors: expenseGroup.vendors,
                configAccounts: expenseGroup.configAccounts,
            });
        } else {
            form.resetFields();
        }
    }, [isEditMode, expenseGroup, form]);

    // Close drawer only on successful create/update
    useEffect(() => {
        if (operationCompleted && !loading && !error) {
            // Refetch all expense groups to get latest values after update
            if (isEditMode) {
                dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
            }
            closeDrawerByAnimation();
            setOperationCompleted(false);
        }
    }, [operationCompleted, loading, error, closeDrawerByAnimation, isEditMode, dispatch]);

    const handleSave = async (values: any) => {
        setOperationCompleted(true);

        // Transform blank values (empty strings) to '-' for backend consistency
        const transformedValues = {
            ...values,
            customers: values.customers?.map((customer: string) =>
                customer === '' ? 'blank' : customer
            ) || [],
            classes: values.classes?.map((classItem: string) =>
                classItem === '' ? 'blank' : classItem
            ) || [],
            locations: values.locations?.map((location: string) =>
                location === '' ? 'blank' : location
            ) || []
        };

        if (isEditMode && expenseGroup) {
            // Update existing expense group
            dispatch(updateExpenseGroupAction({
                id: expenseGroup.id,
                ...transformedValues
            }));
        } else {
            // Create new expense group
            dispatch(createExpenseGroupAction(transformedValues));
        }
    };

    const handleCancel = () => {
        closeDrawerByAnimation();
    };

    // Custom label renderer to make labels bold and move asterisk after text
    const renderLabel = (text: string, required = false) => (
        <span style={{ fontWeight: 'bold' }}>
            {text}
            {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
        </span>
    );

    return (
        <div className={styles.addEditExpenseGroup}>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                className={styles.expenseGroupForm}
            >
                <div className={styles['side-drawer-form__inputs']}>
                    <Form.Item
                        label={renderLabel("Name", true)}
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter expense group name' },
                            { min: 2, message: 'Name must be at least 2 characters' },
                            { max: 255, message: 'Name must not exceed 255 characters' }
                        ]}
                        required={false}
                        className={styles['side-drawer-form__single-input']}
                    >
                        <Input
                            placeholder="Enter expense group name"
                            className={styles['side-drawer-form__single-input--input']}
                            maxLength={255}
                            showCount={!expenseGroup?.isUsed}
                            disabled={expenseGroup?.isUsed}
                            suffix={
                                expenseGroup?.isUsed ? (
                                    <Tooltip title="This expense group is already used and name cannot be modified">
                                        <InfoCircleOutlined style={{ color: '#999' }} />
                                    </Tooltip>
                                ) : null
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label={renderLabel("Description")}
                        name="description"
                        rules={[
                            { max: 500, message: 'Description must not exceed 500 characters' }
                        ]}
                        className={styles['side-drawer-form__single-input']}
                    >
                        <Input.TextArea
                            placeholder="Enter expense group description"
                            rows={3}
                            showCount
                            maxLength={500}
                            className={styles['side-drawer-form__single-input--textarea']}
                        />
                    </Form.Item>

                    {/* Accounts and Customers side by side */}
                    <div className={styles.formRow}>
                        <Form.Item
                            label={renderLabel("Accounts")}
                            name="accounts"
                            // rules={[
                            //     {
                            //         required: true,
                            //         message: "Please select at least one account.",
                            //         validator: (_, value) => {
                            //             if (value && value.length > 0) {
                            //                 return Promise.resolve();
                            //             }
                            //             return Promise.reject(new Error("Please select at least one account."));
                            //         },
                            //     },
                            // ]}
                            required={false}
                            className={`${styles['side-drawer-form__single-input']} ${styles.formItem}`}
                        >
                            <TreeSelect
                                multiple
                                showSearch
                                allowClear
                                treeDefaultExpandAll
                                treeData={treeData.accounts}
                                placeholder="Select accounts"
                                style={{ width: '100%' }}
                                dropdownStyle={{ minWidth: 350 }}
                                size="large"
                                treeLine
                                className={styles.customTreeSelect}
                                filterTreeNode={(inputValue, treeNode) =>
                                    treeNode?.props?.title
                                        ?.toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                }
                                maxTagCount={0}
                                onDropdownVisibleChange={(open) => {
                                    setAccountsTooltipDisabled(open);
                                }}
                                maxTagPlaceholder={() => {
                                    const values = form.getFieldValue('accounts') || [];
                                    if (values.length === 0) return 'Accounts';
                                    const labels = values.map((v: string) => getOptionLabel(v, treeData.accounts));
                                    return (
                                        <Tooltip
                                            title={
                                                <div className="filter-tooltip-content">
                                                    {labels.map((l: string, i: number) => (
                                                        <div
                                                            key={i}
                                                            className="filter-tooltip-item"
                                                            style={{
                                                                borderBottom:
                                                                    i !== labels.length - 1 ? "1px solid rgba(52, 71, 53, 0.1)" : "none",
                                                            }}
                                                        >
                                                            {l}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            placement="bottom"
                                            overlayClassName="filter-tooltip"
                                            visible={accountsTooltipDisabled ? false : undefined}
                                            trigger={accountsTooltipDisabled ? [] : ['hover']}
                                        >
                                            Accounts ({values.length})
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={renderLabel("Customers")}
                            name="customers"
                            className={`${styles['side-drawer-form__single-input']} ${styles.formItem}`}
                        >
                            <TreeSelect
                                multiple
                                showSearch
                                allowClear
                                treeDefaultExpandAll
                                treeData={treeData.customers}
                                placeholder="Select customers"
                                style={{ width: '100%' }}
                                size="large"
                                dropdownStyle={{ minWidth: 350 }}
                                treeLine
                                className={styles.customTreeSelect}
                                filterTreeNode={(inputValue, treeNode) =>
                                    treeNode?.props?.title
                                        ?.toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                }
                                maxTagCount={0}
                                onDropdownVisibleChange={(open) => {
                                    setCustomersTooltipDisabled(open);
                                }}
                                maxTagPlaceholder={() => {
                                    const values = form.getFieldValue('customers') || [];
                                    if (values.length === 0) return 'Customers';
                                    const labels = values.map((v: string) =>
                                        v === '' ? 'Blank Customer' : getOptionLabel(v, treeData.customers)
                                    );
                                    return (
                                        <Tooltip
                                            title={
                                                <div className="filter-tooltip-content">
                                                    {labels.map((l: string, i: number) => (
                                                        <div
                                                            key={i}
                                                            className="filter-tooltip-item"
                                                            style={{
                                                                borderBottom:
                                                                    i !== labels.length - 1 ? "1px solid rgba(52, 71, 53, 0.1)" : "none",
                                                            }}
                                                        >
                                                            {l}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            placement="bottom"
                                            overlayClassName="filter-tooltip"
                                            visible={customersTooltipDisabled ? false : undefined}
                                            trigger={customersTooltipDisabled ? [] : ['hover']}
                                        >
                                            Customers ({values.length})
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Form.Item>
                    </div>

                    {/* Classes and Locations side by side */}
                    <div className={styles.formRow}>
                        <Form.Item
                            label={renderLabel("Classes")}
                            name="classes"
                            className={`${styles['side-drawer-form__single-input']} ${styles.formItem}`}
                        >
                            <TreeSelect
                                multiple
                                showSearch
                                allowClear
                                treeDefaultExpandAll
                                treeData={treeData.classes}
                                placeholder="Select classes"
                                style={{ width: '100%' }}
                                size="large"
                                dropdownStyle={{ minWidth: 350 }}
                                treeLine
                                className={styles.customTreeSelect}
                                filterTreeNode={(inputValue, treeNode) =>
                                    treeNode?.props?.title
                                        ?.toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                }
                                maxTagCount={0}
                                onDropdownVisibleChange={(open) => {
                                    setClassesTooltipDisabled(open);
                                }}
                                maxTagPlaceholder={() => {
                                    const values = form.getFieldValue('classes') || [];
                                    if (values.length === 0) return 'Classes';
                                    const labels = values.map((v: string) =>
                                        v === '' ? 'Blank Class' : getOptionLabel(v, treeData.classes)
                                    );
                                    return (
                                        <Tooltip
                                            title={
                                                <div className="filter-tooltip-content">
                                                    {labels.map((l: string, i: number) => (
                                                        <div
                                                            key={i}
                                                            className="filter-tooltip-item"
                                                            style={{
                                                                borderBottom:
                                                                    i !== labels.length - 1 ? "1px solid rgba(52, 71, 53, 0.1)" : "none",
                                                            }}
                                                        >
                                                            {l}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            placement="bottom"
                                            overlayClassName="filter-tooltip"
                                            visible={classesTooltipDisabled ? false : undefined}
                                            trigger={classesTooltipDisabled ? [] : ['hover']}
                                        >
                                            Classes ({values.length})
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={renderLabel("Locations")}
                            name="locations"
                            className={`${styles['side-drawer-form__single-input']} ${styles.formItem}`}
                        >
                            <TreeSelect
                                multiple
                                showSearch
                                allowClear
                                treeDefaultExpandAll
                                treeData={treeData.locations}
                                placeholder="Select locations"
                                style={{ width: '100%' }}
                                size="large"
                                dropdownStyle={{ minWidth: 350 }}
                                treeLine
                                className={styles.customTreeSelect}
                                filterTreeNode={(inputValue, treeNode) =>
                                    treeNode?.props?.title
                                        ?.toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                }
                                maxTagCount={0}
                                onDropdownVisibleChange={(open) => {
                                    setLocationsTooltipDisabled(open);
                                }}
                                maxTagPlaceholder={() => {
                                    const values = form.getFieldValue('locations') || [];
                                    if (values.length === 0) return 'Locations';
                                    const labels = values.map((v: string) =>
                                        v === '' ? 'Blank Location' : getOptionLabel(v, treeData.locations)
                                    );
                                    return (
                                        <Tooltip
                                            title={
                                                <div className="filter-tooltip-content">
                                                    {labels.map((l: string, i: number) => (
                                                        <div
                                                            key={i}
                                                            className="filter-tooltip-item"
                                                            style={{
                                                                borderBottom:
                                                                    i !== labels.length - 1 ? "1px solid rgba(52, 71, 53, 0.1)" : "none",
                                                            }}
                                                        >
                                                            {l}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            placement="bottom"
                                            overlayClassName="filter-tooltip"
                                            visible={locationsTooltipDisabled ? false : undefined}
                                            trigger={locationsTooltipDisabled ? [] : ['hover']}
                                        >
                                            Locations ({values.length})
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Form.Item>
                    </div>

                    {/* Vendors - half width, same row pattern as Classes/Locations */}
                    <div className={styles.formRow}>
                        <Form.Item
                            label={renderLabel("Vendors")}
                            name="vendors"
                            className={`${styles['side-drawer-form__single-input']} ${styles.formItem}`}
                        >
                            <TreeSelect
                                multiple
                                showSearch
                                allowClear
                                treeDefaultExpandAll
                                treeData={treeData.vendors}
                                placeholder="Select vendors"
                                style={{ width: '100%' }}
                                size="large"
                                dropdownStyle={{ minWidth: 350 }}
                                treeLine
                                className={styles.customTreeSelect}
                                filterTreeNode={(inputValue, treeNode) =>
                                    treeNode?.props?.title
                                        ?.toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                }
                                maxTagCount={0}
                                onDropdownVisibleChange={(open) => {
                                    setVendorsTooltipDisabled(open);
                                }}
                                maxTagPlaceholder={() => {
                                    const values = form.getFieldValue('vendors') || [];
                                    if (values.length === 0) return 'Vendors';
                                    const labels = values.map((v: string) => getOptionLabel(v, treeData.vendors));
                                    return (
                                        <Tooltip
                                            title={
                                                <div className="filter-tooltip-content">
                                                    {labels.map((l: string, i: number) => (
                                                        <div
                                                            key={i}
                                                            className="filter-tooltip-item"
                                                            style={{
                                                                borderBottom:
                                                                    i !== labels.length - 1 ? "1px solid rgba(52, 71, 53, 0.1)" : "none",
                                                            }}
                                                        >
                                                            {l}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            placement="bottom"
                                            overlayClassName="filter-tooltip"
                                            visible={vendorsTooltipDisabled ? false : undefined}
                                            trigger={vendorsTooltipDisabled ? [] : ['hover']}
                                        >
                                            Vendors ({values.length})
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Form.Item>
                        <div className={styles.formItem} aria-hidden />
                    </div>
                </div>

                <div className={styles['side-drawer-form__buttons']}>
                    <Form.Item>
                        <button
                            className={`${styles['side-drawer-form__buttons--save']} ${loading && 'pointer-event-none'
                                }`}
                            type="submit"
                        >
                            {loading ? (
                                <img
                                    src="/assets/gifs/loading-black.gif"
                                    height={40}
                                />
                            ) : (
                                isEditMode ? 'Update' : 'Save'
                            )}
                        </button>
                    </Form.Item>
                    <Form.Item>
                        <button
                            className={`${styles['side-drawer-form__buttons--cancel']}`}
                            onClick={handleCancel}
                            disabled={loading}
                            type="button"
                        >
                            Cancel
                        </button>
                    </Form.Item>
                </div>
            </Form>
        </div>
    );
};

export default AddEditExpenseGroup;
